import { getSyncableElements, SyncableSchemadrawElement } from "../data";
import type {
    SchemadrawElement,
    FileId,
    OrderedSchemadrawElement,
} from "../element/types";
import { decompressData } from "./encode";
import { decryptData, encryptData } from "./encryption";
import { restoreElements } from "./restore";
import { getSceneVersion } from "../element";
import { MIME_TYPES, TX_COS, BACKEND_API_URL } from "../utils/constants";
import {
    AppState,
    BinaryFileData,
    BinaryFileMetadata,
    DataURL
} from "../utils/types";
import Portal from "../containers/Board/Collab/Portal";
import { reconcileElements } from "./reconcile";
import COS from 'cos-js-sdk-v5';
import type { RemoteSchemadrawElement } from "../data/reconcile";


// private
// -----------------------------------------------------------------------------

interface TxcosStoredScene {
    sceneVersion: number;
    iv: Uint8Array;
    ciphertext: ArrayBuffer;
}

const encryptElements = async (
    key: string,
    elements: readonly SchemadrawElement[],
): Promise<{ ciphertext: ArrayBuffer; iv: Uint8Array }> => {
    const json = JSON.stringify(elements);
    const encoded = new TextEncoder().encode(json);
    const { encryptedBuffer, iv } = await encryptData(key, encoded);

    return { ciphertext: encryptedBuffer, iv };
};

const decryptElements = async (
    data: TxcosStoredScene,
    roomKey: string,
): Promise<readonly SchemadrawElement[]> => {
    const ciphertext = data.ciphertext;
    const iv = data.iv;

    const decrypted = await decryptData(iv, ciphertext, roomKey);
    const decodedData = new TextDecoder("utf-8").decode(
        new Uint8Array(decrypted),
    );
    return JSON.parse(decodedData);
};

class TxcosSceneVersionCache {
    private static cache = new WeakMap<SocketIOClient.Socket, number>();
    static get = (socket: SocketIOClient.Socket) => {
        return TxcosSceneVersionCache.cache.get(socket);
    };
    static set = (
        socket: SocketIOClient.Socket,
        elements: readonly SyncableSchemadrawElement[],
    ) => {
        TxcosSceneVersionCache.cache.set(socket, getSceneVersion(elements));
    };
}

export const isSavedToTxcos = (
    portal: Portal,
    elements: readonly SchemadrawElement[],
): boolean => {
    if (portal.socket && portal.roomId && portal.roomKey) {
        const sceneVersion = getSceneVersion(elements);

        return TxcosSceneVersionCache.get(portal.socket) === sceneVersion;
    }
    // if no room exists, consider the room saved so that we don't unnecessarily
    // prevent unload (there's nothing we could do at that point anyway)
    return true;
};

const createTxcosSceneDocument = async (
    elements: readonly SyncableSchemadrawElement[],
    roomKey: string,
) => {
    const sceneVersion = getSceneVersion(elements);
    const { ciphertext, iv } = await encryptElements(roomKey, elements);
    return {
        sceneVersion,
        ciphertext: new Uint8Array(ciphertext),
        iv: iv,
    } as unknown as TxcosStoredScene;
};

export const saveToTxcos = async (
    portal: Portal,
    elements: readonly SyncableSchemadrawElement[],
    appState: AppState,
) => {
    const { roomId, roomKey, socket } = portal;
    if (
        // bail if no room exists as there's nothing we can do at this point
        !roomId ||
        !roomKey ||
        !socket ||
        isSavedToTxcos(portal, elements)
    ) {
        return false;
    }

    let respData = ""
    await fetch(`${BACKEND_API_URL.SCENE_GET_URL}?roomID=${roomId}`)
        .then(response => response.json())
        .then(data => respData = data)

    let savedData = {
        elements: elements,
        reconciledElements: elements,
    };
    if (respData === "") {
        const sceneDocument = await createTxcosSceneDocument(
            elements,
            roomKey,
        );

        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-RapidAPI-Key': 'your-api-key',
            },
            body: JSON.stringify(sceneDocument)
        };
        await fetch(`${BACKEND_API_URL.SCENE_CREATE_URL}`, options);
        savedData = {
            elements: elements,
            reconciledElements: [],
        }
    } else {
        const prevStoredScene = JSON.parse(respData);
        const prevStoredElements = getSyncableElements(
            restoreElements(await decryptElements(prevStoredScene, roomKey), null),
        );

        const reconciledElements = getSyncableElements(
            reconcileElements(
                elements,
                prevStoredElements as OrderedSchemadrawElement[] as RemoteSchemadrawElement[],
                appState,
            ),
        );

        const storedScene = await createTxcosSceneDocument(
            reconciledElements,
            roomKey,
        );

        const options = {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-RapidAPI-Key': 'your-api-key',
            },
            body: JSON.stringify(storedScene)
        };
        await fetch(`${BACKEND_API_URL.SCENE_UPDATE_URL}`, options);
        savedData = {
            elements: elements,
            reconciledElements: reconciledElements,
        }
    }

    TxcosSceneVersionCache.set(socket, savedData.elements);

    return { reconciledElements: savedData.reconciledElements };
};

export const loadFromTxcos = async (
    roomId: string,
    roomKey: string,
    socket: SocketIOClient.Socket | null,
): Promise<readonly SyncableSchemadrawElement[] | null> => {
    let respData = ""
    await fetch(`${BACKEND_API_URL.SCENE_GET_URL}?roomid=${roomId}`)
        .then(response => response.json())
        .then(data => respData = data)
    if (respData === "") {
        return null;
    }
    const storedScene = JSON.parse(respData);
    const elements = getSyncableElements(
        restoreElements(await decryptElements(storedScene, roomKey), null),
    );

    if (socket) {
        TxcosSceneVersionCache.set(socket, elements);
    }

    return elements;
};


export const saveFilesToTxcos = async ({
    prefix,
    files,
}: {
    prefix: string;
    files: { id: FileId; buffer: Uint8Array }[];
}) => {
    const txcos = new COS({
        SecretId: TX_COS.ACCESS_KEY_ID,
        SecretKey: TX_COS.ACCESS_KEY_SECRET,
    });

    const erroredFiles = new Map<FileId, true>();
    const savedFiles = new Map<FileId, true>();

    await Promise.all(
        files.map(async ({ id, buffer }) => {
            try {
                const result = await txcos
                    .putObject(
                        {
                            Bucket: TX_COS.BUCKET,
                            Region: TX_COS.REGION,
                            Key: `${prefix}/${id}`,
                            Body: new Blob([buffer], {
                                type: MIME_TYPES.binary,
                            }),
                        }
                    );
                savedFiles.set(id, true);
            } catch (error: any) {
                console.log(error)
                erroredFiles.set(id, true);
            }
        }),
    );

    return { savedFiles, erroredFiles };
};

export const loadFilesFromTxcos = async (
    prefix: string,
    decryptionKey: string,
    filesIds: readonly FileId[],
) => {
    const loadedFiles: BinaryFileData[] = [];
    const erroredFiles = new Map<FileId, true>();

    await Promise.all(
        [...new Set(filesIds)].map(async (id) => {
            try {
                const url = `${TX_COS.CACHE_URI}/${prefix}/${id}`;
                const response = await fetch(`${url}?alt=media`);
                if (response.status < 400) {
                    const arrayBuffer = await response.arrayBuffer();
                    const { data, metadata } = await decompressData<BinaryFileMetadata>(
                        new Uint8Array(arrayBuffer),
                        {
                            decryptionKey,
                        },
                    );

                    const dataURL = new TextDecoder().decode(data) as DataURL;

                    loadedFiles.push({
                        mimeType: metadata.mimeType || MIME_TYPES.binary,
                        id,
                        dataURL,
                        created: metadata?.created || Date.now(),
                        lastRetrieved: metadata?.created || Date.now(),
                    });
                } else {
                    erroredFiles.set(id, true);
                }
            } catch (error: any) {
                erroredFiles.set(id, true);
                console.error(error);
            }
        }),
    );

    return { loadedFiles, erroredFiles };
};

export const saveImageToTxcos = async ({
    prefix,
    name,
    file,
}: {
    prefix: string;
    name: string;
    file: Blob;
}) => {
    const txcos = new COS({
        SecretId: TX_COS.ACCESS_KEY_ID,
        SecretKey: TX_COS.ACCESS_KEY_SECRET,
    });

    try {
        const result = await txcos
            .uploadFile({
                Bucket: TX_COS.BUCKET,
                Region: TX_COS.REGION,
                Key: `${prefix}/${name}`,
                Body: file,
            });
    } catch (error: any) {
        console.log(error)
    }
};

export const copyImageToTxcos = async ({
    targetName,
    sourceName,
}: {
    targetName: string;
    sourceName: string;
}) => {
    const txcos = new COS({
        SecretId: TX_COS.ACCESS_KEY_ID,
        SecretKey: TX_COS.ACCESS_KEY_SECRET,
    });

    try {
        const result = await txcos
            .putObjectCopy(
                {
                    Bucket: TX_COS.BUCKET,
                    Region: TX_COS.REGION,
                    Key: targetName,
                    CopySource: `${TX_COS.END_POINT}/${sourceName}`,
                }
            );
    } catch (error: any) {
        console.log(error)
    }
};

export const deleteImageFromTxcos = async ({
    name,
}: {
    name: string;
}) => {
    const txcos = new COS({
        SecretId: TX_COS.ACCESS_KEY_ID,
        SecretKey: TX_COS.ACCESS_KEY_SECRET,
    });

    console.log(name)

    try {
        const result = await txcos
            .deleteObject(
                {
                    Bucket: TX_COS.BUCKET,
                    Region: TX_COS.REGION,
                    Key: name,
                }
            );
        console.log(result);
    } catch (error: any) {
        console.log(error)
    }
};